import React from 'react';

interface ProgressIndicatorProps {
  currentStep: number;
  totalSteps: number;
  progress: number;
  isReviewStep: boolean;
}

export function ProgressIndicator({ currentStep, totalSteps, progress, isReviewStep }: ProgressIndicatorProps) {
  return (
    <div className="mb-8">
      {/* Progress Bar */}
      <div className="bg-white/20 rounded-full h-3 mb-4 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-500 to-amber-600 transition-all duration-500 ease-out shadow-lg"
          style={{ width: `${progress}%` }}
        >
          {/* Animated shine effect */}
          <div className="h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
        </div>
      </div>

      {/* Step Indicators */}
      <div className="flex items-center justify-between">
        {/* Question Steps */}
        {Array.from({ length: totalSteps }, (_, index) => {
          const stepNumber = index + 1;
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep && !isReviewStep;
          
          return (
            <div key={index} className="flex flex-col items-center">
              {/* Step Circle */}
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 mb-2
                  ${isCompleted
                    ? 'bg-amber-500 text-white shadow-lg'
                    : isCurrent
                      ? 'bg-white text-amber-600 shadow-lg ring-2 ring-amber-500'
                      : 'bg-white/30 text-white/70'
                  }
                `}
              >
                {isCompleted ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  stepNumber
                )}
              </div>

              {/* Step Label - only show on larger screens */}
              <span className={`
                hidden sm:block text-xs font-medium transition-colors duration-300
                ${(isCompleted || isCurrent) ? 'text-white' : 'text-white/60'}
              `}>
                Step {stepNumber}
              </span>
            </div>
          );
        })}

        {/* Review Step */}
        <div className="flex flex-col items-center">
          <div
            className={`
              w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 mb-2
              ${isReviewStep
                ? 'bg-white text-amber-600 shadow-lg ring-2 ring-amber-500'
                : currentStep >= totalSteps
                  ? 'bg-amber-500 text-white shadow-lg'
                  : 'bg-white/30 text-white/70'
              }
            `}
          >
            {currentStep >= totalSteps && !isReviewStep ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </div>

          <span className={`
            hidden sm:block text-xs font-medium transition-colors duration-300
            ${isReviewStep ? 'text-white' : 'text-white/60'}
          `}>
            Review
          </span>
        </div>
      </div>

      {/* Progress Text */}
      <div className="text-center mt-4">
        <span className="text-white/80 text-sm">
          {isReviewStep ? (
            'Review your answers before submission'
          ) : (
            `Question ${currentStep + 1} of ${totalSteps}`
          )}
        </span>
        <div className="text-white/60 text-xs mt-1">
          {progress}% Complete
        </div>
      </div>
    </div>
  );
}